import * as React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { IntlProvider } from 'react-intl-next';
import type { State } from 'state';
import { getLocale } from 'state/selectors/user';
import { importLocaleMessages, getNormalizedLocale } from './translation-util';
import { withAsync } from 'view/common/with-async';
import { getLanguageFromLocale } from 'util/locale-data';
import { lazyForPaint } from 'react-loosely-lazy';
import { onIntlNextError } from 'providers';

import { promiseWithRetries } from 'util/retry';
import type { MessageFormatElement } from '@formatjs/icu-messageformat-parser';

interface ConnectedProps {
    locale: string;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
type RichTextEditorProps = import('./rich-text-editor-v2').RichTextEditorProps;

/**
 *  (Currently) the editor supports 2 rendering modes in the Portal, it can render either as a comment box or as a field in the request create form.
 *  Both cases can be see as being "semi-uncontrolled" components.
 *
 *  In the case of the comment box, we copy the ADF document to redux UI state on submit
 *  so we can re-populate the editor if the request fails.
 *
 *  In the case of the field, we update Formik's internal state as the user types, and use that value when submitting the form.
 *
 */
const AsyncRichTextEditor = (props: RichTextEditorProps) => {
    const prevProps = React.useRef(props);
    React.useEffect(() => {
        prevProps.current = props;
    });

    // We want to explicitly re-render the editor component after a comment is submitted to clear the previous comments value
    const commentSubmitted =
        props.editorType === 'comment-editor' && prevProps.current.defaultValue !== props.defaultValue;

    const AsyncEditor: React.ComponentType<RichTextEditorProps> = React.useMemo(
        () =>
            withAsync({
                loader: lazyForPaint(() => import(/* webpackChunkName: "rich-text-editor-v2" */ './hocs')),
                LoadingSkeleton: props.LoadingComponent,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.editorType, commentSubmitted]
    );

    return <AsyncEditor {...props} />;
};

const EditorWithTranslations: React.ComponentType<RichTextEditorProps & ConnectedProps> = React.memo(
    // eslint-disable-next-line prefer-arrow-callback
    function EditorWithTranslationsV2(ownProps) {
        const { locale, LoadingComponent = () => null } = ownProps;
        const [intlMessages, setIntlMessages] = React.useState<
            Record<string, string> | Record<string, MessageFormatElement[]>
        >();
        const [error, setError] = React.useState<Error | undefined>();

        React.useEffect(() => {
            promiseWithRetries(() => importLocaleMessages(locale))
                .then((loaded) => {
                    setIntlMessages(loaded);
                })
                .catch((err) => {
                    setError(err);
                });
        }, [locale]);

        if (!intlMessages) {
            return <LoadingComponent error={error} isLoading={!error} />;
        }

        return (
            <IntlProvider messages={intlMessages} locale={getLanguageFromLocale(locale)} onError={onIntlNextError}>
                <AsyncRichTextEditor {...ownProps} />
            </IntlProvider>
        );
    }
);

export const LocalizedAsyncRichTextEditor = (props: RichTextEditorProps & ConnectedProps) => {
    const { locale, LoadingComponent = () => null } = props;

    // If the users locale is english, which is the editors default locale, we can avoid requiring the locale unnecessarily
    if (locale === 'en') {
        return <AsyncRichTextEditor {...props} LoadingComponent={LoadingComponent} />;
    }

    return <EditorWithTranslations {...props} LoadingComponent={LoadingComponent} />;
};

const selector = createSelector(
    (state: State) => getLocale(state),
    (locale) => ({ locale: getNormalizedLocale(locale) })
);

export default connect(selector)(LocalizedAsyncRichTextEditor);
